import { Mail, MapPin, Building2 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import EarlyAccessForm from "@/components/EarlyAccessForm";
import { ExpandableDisclaimer } from "@/components/Compliance";
import usePageMeta from "@/hooks/usePageMeta";
import { COMPANY } from "@/lib/site";

export default function Contact() {
  usePageMeta({
    title: "Contact — Adviserz | Quant Core Technologies Private Limited",
    description:
      "Contact Quant Core Technologies Private Limited, the company behind the Adviserz market intelligence platform. Registered office in Mohali, Punjab.",
    canonical: "https://adviserz.in/contact",
  });

  return (
    <div className="min-h-screen bg-surface-base text-white">
      <SiteHeader active="contact" />

      <section className="relative overflow-hidden border-b border-white/[0.06]">
        <div className="absolute inset-0 grid-bg-fine opacity-60 grid-radial-mask" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-12 pt-36 md:pt-44 pb-16">
          <div className="overline mb-6">Contact</div>
          <h1 className="font-display font-medium text-[36px] sm:text-5xl tracking-tighter leading-[1.06]">
            Get in touch.
          </h1>
          <p className="mt-6 text-zinc-400 max-w-2xl leading-relaxed">
            For company, partnership, media or platform enquiries, reach the team behind Adviserz.
          </p>
        </div>
      </section>

      <section className="bg-surface-base">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6 space-y-4">
            <Row icon={Building2} k="Company" v={<>
              <div className="text-white">{COMPANY.legalName}</div>
              <div className="mt-1 text-zinc-500 text-sm">Brand: {COMPANY.brand}</div>
            </>} />
            <Row icon={MapPin} k="Registered / Corporate Office" v={<div className="text-zinc-300">{COMPANY.address}</div>} />
            <Row icon={Mail} k="Email" v={
              <a href={`mailto:${COMPANY.email}`} data-testid="contact-email" className="text-white hover:underline">{COMPANY.email}</a>
            } />

            <div className="rounded-lg border border-white/[0.08] bg-white/[0.015] p-6">
              <div className="label-xs mb-3">Please note</div>
              <p className="text-xs text-zinc-400 leading-relaxed">
                We are unable to respond to requests for investment advice, stock or derivatives recommendations,
                portfolio guidance or trading assistance. {COMPANY.brand} is a technology and analytics platform and is
                currently not registered with SEBI as a Research Analyst or Investment Adviser.
              </p>
            </div>

            <ExpandableDisclaimer testid="contact-disclaimer" />
          </div>

          <div className="lg:col-span-6">
            <div className="surface-elevated rounded-xl p-8">
              <div className="label-xs mb-2">Early access</div>
              <h2 className="font-display font-medium text-2xl tracking-tight">Register your interest</h2>
              <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                Leave your details and we will notify you when platform access opens.
              </p>
              <EarlyAccessForm />
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function Row({ icon: Icon, k, v }) {
  return (
    <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-6 flex items-start gap-4">
      <div className="shrink-0 w-10 h-10 rounded-md border border-white/10 bg-white/[0.03] grid place-items-center">
        <Icon className="w-4 h-4 text-ai-accent" />
      </div>
      <div className="min-w-0">
        <div className="label-xs text-zinc-500 mb-2">{k}</div>
        {v}
      </div>
    </div>
  );
}
